import React, { useState, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { APIContext } from '../Contexts/APIContext';
import FilterSelectModal from './FilterSelectModal';
import SimilarWords from './SimilarWords';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';
import SalesFilters from './ReworkedFilters/SalesFilters/SalesFilters';
import OperationsFilters from './ReworkedFilters/OperationsFilters/OperationsFilters';
import VulnerabilitiesFilters from './ReworkedFilters/VulnerabilitiesFilters/VulnerabilitiesFilters';
import QaFilters from './ReworkedFilters/QaFilters/QaFilters';
import ComplaintsFilters from './ReworkedFilters/ComplaintsFilters/ComplaintsFilters';
import AdvancedFilters from './ReworkedFilters/AdvancedFilters/AdvancedFilter';
import DateTimeFilters from './ReworkedFilters/DateTimeFilters/DateTimeFilters';
import ReportCodeFilters from './ReworkedFilters/ReportCodeFilters/ReportCodeFilters';
import CallFilters from './ReworkedFilters/CallFilters/CallFilters';
import CallTableViewModel from '../ViewModels/CallTableViewModel';
import TranscriptFilters from './ReworkedFilters/TranscriptFilters/TranscriptFilters';
import OrderBy from './ReworkedFilters/OrderBy/OrderBy';
import CustomerSentimentFilters from './ReworkedFilters/SentimentFilters/CustomerSentimentFilter';
import ScorecardAnswersFilters from './ReworkedFilters/ScorecardAnswerFilters/ScorecardAnswers';
import QAScorecardFilter from './ReworkedFilters/QAScorecardFilter/QAScorecardFilter';
import SalesAIFilters from './ReworkedFilters/SalesAIFilters/SalesAI';
import InsightFilters from './ReworkedFilters/InsightFilters/Insight';
import AIFlaggedIssuesFilters from './ReworkedFilters/AIFlaggedIssuesFilters/AIFlaggedIssues';
import CustomerFilters from './ReworkedFilters/CustomerFilter/CustomerFilters';
import ClientDisplayChip from './ReworkedFilters/Utility/ClientDisplay';
import AgentSentimentFilters from './ReworkedFilters/SentimentFilters/AgentSentimentFilter';
import SavedSelectedFiltersButton from '../Menu/SaveSelectedFiltersButton';
import DialogContentText from '@mui/material/DialogContentText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import GdprFilters from './ReworkedFilters/VoiceOfTheCustomerFilter/GDPRFilters';
import QAComplaintsFilters from './ReworkedFilters/ComplaintsFilters/QAComplaintsFilters';
import VOCFilters from './ReworkedFilters/VoiceOfTheCustomerFilter/VOCFilters';
import AINoteFilters from './ReworkedFilters/AINoteFilters/AINoteFilters';
import CXAIFilters from './ReworkedFilters/CXAIFilters/CXAIFilters';

interface IFilterBarProps {
    client: string;
    updateFilters: (filters: CallTableViewModel, apply?: boolean) => void;
    readonly: boolean;
    loaded: boolean;
    isRTM: boolean;
    isClient: boolean;
    isAgent: boolean;
    rowsCount: number;
    bulkDownload?: () => void;
    toggleNewFilter?: () => void;
    similarWords: string[];
    isReport: boolean;
    showDownloadButton: boolean;
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function FilterBar({ client, updateFilters, readonly, loaded, isRTM, isClient, rowsCount, bulkDownload, toggleNewFilter, similarWords, isAgent, isReport, showDownloadButton, callTableViewModel, setCallTableViewModel }: IFilterBarProps) {
    const { isAdmin, isQA, isClientAIEnabled, isClientTranscriptsEnabled } = useContext(APIContext);
    const [bulkModal, setBulkModal] = useState(false);

    const openCloseBulkModal = () => setBulkModal(prevState => !prevState);

    const closeBulkModelDialog = () => {
        setBulkModal(false);
        if (bulkDownload !== undefined) {
            bulkDownload();
        }
    }

    const applyFiltersClick = (e) => {
        const newFilters = { ...callTableViewModel };
        newFilters.filters.pageNumber = 1;

        updateFiltersFunc(newFilters, true);
    }

    const updateFiltersFunc = (filters: CallTableViewModel, apply?: boolean) => {
        const newFilters = { ...filters };
        updateFilters(newFilters, apply);
    }

    return (
        <Grid container justifyContent="space-between" alignItems="center" spacing={0} style={{ padding: '8px' }}>
            <Grid size={!isClient ? 9.0 : 10.5}>
                <Grid container spacing={1}>
                    <ClientDisplayChip name={callTableViewModel.filters.client} />
                    <DateTimeFilters
                        isAgent={isAgent}
                        callTableViewModel={callTableViewModel}
                        setCallTableViewModel={setCallTableViewModel} />
                    {
                        (!isClient || isClientTranscriptsEnabled) &&
                        <TranscriptFilters
                            isAgent={isAgent}
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.salesFilters.isActive &&
                        <SalesFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.qaFilters.isActive &&
                        <QaFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.qaComplaintsFilters.isActive &&
                        <QAComplaintsFilters
                            isAgent={isAgent}
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.operationsFilters.isActive &&
                        <OperationsFilters
                            isAgent={isAgent}
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.customerFilters.isActive &&
                        <CustomerFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.vulnerabilitiesFilters.isActive &&
                        <VulnerabilitiesFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.complaintsFilters.isActive &&
                        <ComplaintsFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.reportCodeFilters.isActive &&
                        <ReportCodeFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.callFilters.isActive &&
                        <CallFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.advancedFilters.isActive &&
                        <AdvancedFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.vocFilters.isActive &&
                        <VOCFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.gdprFilters.isActive &&
                        <GdprFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.aiFlaggedIssues.isActive &&
                        <AIFlaggedIssuesFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.aiNoteFilters.isActive &&
                        <AINoteFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.salesAI.isActive &&
                        <SalesAIFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.cxaiFilters.isActive &&
                        <CXAIFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.insight.isActive &&
                        <InsightFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.scorecardAnswer.isActive &&
                        <ScorecardAnswersFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.qaScorecardFilter.isActive &&
                        <QAScorecardFilter
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.customerSentimentFilters.isActive &&
                        <CustomerSentimentFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    {
                        callTableViewModel.filters.agentSentimentFilters.isActive &&
                        <AgentSentimentFilters
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                    }
                    <OrderBy
                        callTableViewModel={callTableViewModel}
                        setCallTableViewModel={setCallTableViewModel} />
                    {!readonly &&
                        <FilterSelectModal isRTM={isRTM} isClient={isClient} isQA={isQA} isAdmin={isAdmin} isAgent={isAgent}
                            isClientAIEnabled={isClientAIEnabled} isReport={isReport}
                            callTableViewModel={callTableViewModel} setCallTableViewModel={setCallTableViewModel} />
                    }
                </Grid>
                {
                    similarWords.length > 0 &&
                    <Grid size={12} paddingTop={1}>
                        <SimilarWords similarWords={similarWords} />
                    </Grid>
                }
            </Grid>
            <Grid container size={!isClient ? 3.0 : 1.5} justifyContent="flex-end">
                {!readonly &&
                    <React.Fragment>
                        {isAdmin && showDownloadButton &&
                            <Tooltip title="Bulk Download" placement="top">
                                <Button size="small" variant="contained" color="primary" style={{ marginRight: '8px', boxShadow: '0 0 0' }} disabled={rowsCount === 0} onClick={openCloseBulkModal}><DownloadIcon /></Button>
                            </Tooltip>
                        }
                        {!isAgent &&
                            <SavedSelectedFiltersButton
                                callTableViewModel={callTableViewModel}
                                toggleNewFilter={toggleNewFilter} />
                        }
                    </React.Fragment>
                }
                <Button size="small" variant="contained" color="secondary" onClick={applyFiltersClick} sx={{ marginLeft: '8px', boxShadow: '0 0 0' }}>Apply Filters</Button>
            </Grid>

            <Dialog open={bulkModal}>
                <DialogContent>
                    {
                        callTableViewModel.filters.dateTimeFilters.preset === 'Today'
                            ?
                            <DialogContentText>Bulk download does not work intra-day.</DialogContentText>
                            :
                            <DialogContentText>Downloading call recordings in bulk will skip any from today and may take a few minutes. Only the first 250 calls will be downloaded.</DialogContentText>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={openCloseBulkModal}
                        color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={closeBulkModelDialog}
                        color="primary"
                        disabled={callTableViewModel.filters.dateTimeFilters.preset === 'Today'}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid >
    );
}